import { ChangeEvent, Dispatch, SetStateAction, useMemo, useState } from "react"
import { Box, Button, Checkbox, Dialog, FormControl, FormControlLabel, Radio, RadioGroup, Switch, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from "@mui/material"
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import CloseModalButton from "../../../../components/CloseModalButton"
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../../components/Dialog.styled"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Kit, KitItem, OpenModal, RefillData, RequestData } from "./types"
import { getOrderedRefillItems, getRefillRowColor, getSelectedItems, isOrderProcessing } from "./utils/helpers"
import { LightTooltip } from "../../dentalenhancements/components/ToolTip"
import ExpirationDate from "./ExpirationDate"
import { User } from "../../../../redux/user"
import { sendRefillRequest } from "../../../../services/medsDevicesService"
import { addError } from "../../../../redux/alerts"
import { useAppDispatch } from "../../../../redux/store"
import { KitType, RefillFrequency } from "./enums"

type KitBuilderModal = {
  kit: Kit,
  kitItems: KitItem[],
  type: KitType,
  currentUser: User | null,
  customerNumber: string,
  openModal: OpenModal,
  isUpdatingItems: boolean,
  setOpenModal: Dispatch<SetStateAction<OpenModal>>,
  setIsUpdatingItems: Dispatch<SetStateAction<boolean>>,
  fetchItems: (customerNumber: string) => Promise<void>,
}

const KitBuilderModal = (props: KitBuilderModal) => {
  const {
    kit,
    kitItems,
    type,
    currentUser,
    customerNumber,
    openModal,
    isUpdatingItems,
    setOpenModal,
    setIsUpdatingItems,
    fetchItems,
   } = props

  const refillFrequency = openModal.type
  const defaultChecked = refillFrequency === RefillFrequency.Recurring

  const theme = useTheme()
  const dispatch = useAppDispatch()
  const sortedItems = useMemo(() => {
    return getOrderedRefillItems(kitItems)
  }, [kitItems])

  const defaultSelected = useMemo(() => {
    const selected = refillFrequency === RefillFrequency.Recurring
      ? getSelectedItems(kitItems)
      : new Map<number, KitItem>()

    return selected
  }, [kitItems, refillFrequency])

  const [frequency, setFrequency] = useState(refillFrequency)
  const [refillAll, setRefillAll] = useState(defaultChecked)
  const [selectedItems, setSelectedItems] = useState<Map<number, KitItem>>(defaultSelected)
  const [refillConfirmed, setRefillConfirmed] = useState(false)

  const handleClose = () => setOpenModal({ open: false, type: null })

  const handleFrequency = (event: ChangeEvent<HTMLInputElement>, value: string) => {
    const frequency = value as RefillFrequency
    setFrequency(frequency)
  }

  const handleRefillAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    
    const selected = checked
      ? getSelectedItems(kitItems)
      : new Map<number, KitItem>()
    
    setRefillAll(checked)
    setSelectedItems(selected)
  }

  const handleSelectItem = (item: KitItem, checked: boolean) => {
    const cloned = new Map(selectedItems)
  
    if (checked) cloned.set(item.id, item)
    else cloned.delete(item.id)

    setRefillAll(false)
    setSelectedItems(cloned)
  }

  const handleSave = async () => {
    if (refillFrequency && currentUser?.user_id && Boolean(selectedItems.size)) {
      try {
        setIsUpdatingItems(true)

        const items: (KitItem)[] = []
        const itemIds: number[] = []

        selectedItems.forEach((item, itemId) => {
          items.push(item)
          itemIds.push(itemId)
        })

        const requestData: RequestData = {
          kit_id: kit.id,
          user_id: String(currentUser.user_id),
          refill_type: refillFrequency,
        }

        const refillData: RefillData = {
          refill_type: refillFrequency,
          user_id: String(currentUser.user_id),
          kit_type: type,
          serial_number: kit.serialNumber,
          items,
          requestData,
        }

        if (type === KitType.Medication) {
          refillData.requestData['medications'] = itemIds
        } else {
          refillData.requestData['devices'] = itemIds
        }

        await sendRefillRequest(customerNumber, refillData)
        await fetchItems(customerNumber)

        setIsUpdatingItems(false)
        setRefillConfirmed(true)
      } catch (err) {
        console.error(err)
        setIsUpdatingItems(false)
        dispatch(addError('There was an issue with your request, please try again later.'))
      }
    }
  }

  const radioLabels = [
    (    
      <>
        <span>Automatic - The Refill Service is an easy way to keep your practice compliant</span>
        <span className="color-danger">{' (recommended)'}</span>      
      </>
    ),
    <span>One-time - Get refilled one time only</span>
  ]

  const disableSave = !(selectedItems.size) || isUpdatingItems

  return (
    <>
      <Dialog
        open={openModal.open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth={true}
      >
        <CloseModalButton handleClose={handleClose} />
        <StyledDialogTitle>
          <Box className="flex flex-align-center c-g-8">
            <MedicalServicesIcon sx={{ color: theme.palette.error.main }} fontSize="small" />
            <span>Refill Kit { kit.model || '' }</span>
            <span>{` # ${kit.serialNumber}`}</span>            
          </Box>
        </StyledDialogTitle>
        <StyledDialogContent>
          {
            refillConfirmed
              ? (
                  <Box>
                    <h1 className="flex flex-column center">
                      <Box>
                        <CheckCircleIcon sx={{ color: theme.palette.success.main, '& .MuiSvgIcon-root': { fontSize: '5rem' } }} />
                      </Box>
                      <Box>Your refill request has been received.</Box>
                      <Box>A customer service representative will contact you shortly to confirm your order.</Box>
                    </h1>
                  </Box>
                )
              : (
                  <>
                    <Box className="m-b-16">
                      Complete this refill form.
                    </Box>
                    <Box className="m-b-16">
                      You will be contacted shortly by a member of our customer service team to confirm your order.
                    </Box>          
                    <Box className="kit-builder step-one m-b-16">
                      <Box>
                        <span className="bold">Step 1:</span>
                        <span>{' Select Your Refill Type'}</span>
                      </Box>
                      <FormControl>
                        <RadioGroup
                          name="kit-builder-frequency-select" 
                          value={frequency}
                          onChange={handleFrequency}
                        >
                          <FormControlLabel value={RefillFrequency.Recurring} control={<Radio />} label={radioLabels[0]} />
                          <FormControlLabel value={RefillFrequency.OneTime} control={<Radio />} label={radioLabels[1]} />
                        </RadioGroup>  
                      </FormControl>
                    </Box>
                    <Box className="kit-builder step-two">
                      <span className="bold">Step 2:</span>
                      <span>{` Select ${type} To Be Refilled`}</span>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ padding: '0 8px', borderTop: 'none' }}>Items</TableCell>
                            <TableCell sx={{ padding: '0 8px', borderTop: 'none' }}>Expiration</TableCell>
                            <TableCell sx={{ padding: '0 8px', borderTop: 'none' }}>
                              Refill All
                              <Checkbox
                                sx={{ marginLeft: '4px '}}
                                checked={refillAll}
                                onChange={handleRefillAll}
                              />
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            sortedItems.map(item => (
                              <TableRow key={`refill-item-row-${item.id}`} sx={{ padding: '0 8px', backgroundColor: getRefillRowColor(item), color: item.discontinued ? 'rgb(150, 150, 150)' : null}}>
                                <TableCell sx={{ padding: '0 8px' }}>
                                  <span>{item.name}</span>
                                  {
                                    isOrderProcessing(item)
                                      &&  (
                                            <span>
                                              {" -"} <strong>Order processing</strong>
                                              <LightTooltip
                                                arrow={true}
                                                placement="top"
                                                title="A refill for this item was approved and is shipping soon."
                                              >
                                                <FontAwesomeIcon className="m-l-8" icon="circle-question" size="sm" />
                                              </LightTooltip>  
                                            </span>
                                          )
                                  }
                                </TableCell>
                                <TableCell sx={{ padding: '0 8px' }}>
                                  <ExpirationDate date={item.expirationDate} status={item.complianceStatus} refill={null} />
                                </TableCell>
                                <TableCell sx={{ padding: '0 8px' }}>
                                  {
                                    item.discontinued
                                      ? <span>discontinued</span>
                                      : (
                                          <Switch
                                            id={`switch-${item.id}`}
                                            color="success"
                                            checked={selectedItems.has(item.id)}
                                            onChange={(event: ChangeEvent<HTMLInputElement>, checked: boolean) => handleSelectItem(item, checked)}
                                          />
                                        )
                                  }
                                </TableCell>
                              </TableRow>
                            ))
                          }
                        </TableBody>
                      </Table>
                    </Box>                  
                  </>
                )
          }
        </StyledDialogContent>
        <StyledDialogFooter>
          {
            refillConfirmed
              ? (
                  <Button onClick={handleClose}>
                    Close
                  </Button>
                )
              : (
                  <>
                    <Button onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={disableSave}>
                      Ok
                    </Button>                  
                  </>
                )
          }

        </StyledDialogFooter>
      </Dialog>
    </>
  )
}

export default KitBuilderModal
